import clsx from 'clsx';
import React from 'react';

import LazyImage from '@components/LazyImage';
import { useAppContext } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import { article_path } from '@routes';
import { ClickTarget, ContentType, click } from '@tracking';
import { getArticleImgSrc } from '@utils/images';

import { Article } from '../../interfaces/homepage';
import Byline from '../Byline';
import IconProTagThumbnail from '../Images/Rebranding/IconProTagThumbnail';
import Highlight from './Highlight';

interface FeaturedArticleProps {
  className?: string;
  featuredArticle: Article;
  isFluid?: boolean;
}

const FeaturedArticle = ({
  className,
  featuredArticle: { authors, freeBlurb, highlight, id, isPro, picture, publishedAt, slug, title },
  isFluid = false,
}: FeaturedArticleProps) => {
  const { env } = useAppContext();
  const { trackEvent, Track } = useEventTracking({
    contentType: ContentType.Article,
    contentId: id,
    creatorIds: authors?.map((author) => author.id) || [],
  });

  return (
    <Track>
      <article
        className={clsx(
          'flex-1 border-0 border-b border-solid border-grayscale-gray px-16 pb-32 sm:px-0',
          className
        )}
      >
        <a
          onClickCapture={() =>
            trackEvent(click({ clickTarget: ClickTarget.FeaturedArticleImage }))
          }
          href={article_path(slug)}
          className={clsx('relative -mx-16 mb-16 block sm:mx-0 sm:mb-24', {
            'h-[calc((100vw)*9/16)] sm:h-[calc((100vw-3.5rem)*9/16)] md:h-[calc(712px*9/16)] md:min-w-[712px] lg:h-[calc(884px*9/16)] lg:min-w-[884px]':
              !isFluid,
            'aspect-[16/9] flex-shrink flex-grow text-[1px] leading-none sm:w-full lg:max-w-[884px]':
              isFluid,
          })}
        >
          {isPro && <IconProTagThumbnail className="absolute -right-16 top-0 sm:right-0" />}
          <LazyImage
            alt={title}
            className="h-auto w-full"
            src={getArticleImgSrc(id, picture, env)}
            sizes="(min-width: 480px) 600px, 300px"
            wrapperClassName="h-full w-full"
          />
        </a>
        {highlight && <Highlight highlight={highlight} />}
        <a
          onClickCapture={() =>
            trackEvent(
              click({ clickTarget: ClickTarget.FeaturedArticleHeadline, clickText: title })
            )
          }
          href={article_path(slug)}
        >
          <h2 className="m-0 font-suisse-works text-headline-xs text-black hover:text-primary-magenta sm:text-headline-md lg:text-headline-lg">
            {title}
          </h2>
          <p className="my-8 line-clamp-2 font-suisse-neue text-body-md text-black sm:text-body-lg">
            {freeBlurb}
          </p>
        </a>
        <Byline authors={authors} publishedAt={publishedAt} />
      </article>
    </Track>
  );
};

export default FeaturedArticle;
